import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/Card';
import { Rejection } from '../components/Rejection';
import { PageHeader } from '../components/Shell';
import { Waiting } from '../components/Waiting';
import { ApiError } from '../net/client';
import {
  batchLabel,
  coverageCards,
  getBatch,
  getBrandDefault,
  getDistributionReport,
  getProductSetting,
  inherited,
  listAllRegions,
  setBatchDistribution,
  type Batch,
  type CoverageCard,
  type DistributionSetting,
  type Inherited,
  type OwnSetting,
  type Region,
} from '../net/regions';
import { useSession } from '../session/useSession';
import { chip, colors, fonts, radius, shell, type, verdict } from '../theme';

/**
 * G5, `design/PortalRegions.dc.html`.
 *
 * Every value below is literal in that board, with its em dashes replaced by
 * rule 7. What the board does not draw is D11: the screen sits under Brand
 * profile and the batch picker opens for `?batch=<id>`, which G4's batch screen
 * will link to; "Used by" is drawn empty because no route counts it; "New
 * region" and "Edit" are drawn but open nothing, because no board draws the
 * form (DEFERRED 4.2).
 */

type Load<T> =
  { status: 'loading' } | { status: 'failed'; error: unknown } | { status: 'loaded'; value: T };

interface Page {
  regions: Region[];
  brandDefault: OwnSetting;
  cards: CoverageCard[];
}

interface Picker {
  batch: Batch;
  productName: string;
  inherits: Inherited;
}

/** `grid-template-columns` of the region table's header and rows. */
const columns = '1.3fr 2fr 1.1fr 0.6fr';

/** The selected option's ground. Drawn on this board only, so it is not a token. */
const selectedGround = '#FDF9F5';

export function Regions() {
  const { api } = useSession();
  const [params] = useSearchParams();
  const batchId = params.get('batch');
  const [page, setPage] = useState<Load<Page>>({ status: 'loading' });
  /** Keyed by the batch it was loaded for, so a changed `?batch=` reads as loading without a synchronous reset. */
  const [loaded, setPicker] = useState<{ batchId: string; load: Load<Picker> } | null>(null);
  const picker: Load<Picker> | null =
    batchId === null ? null : loaded?.batchId === batchId ? loaded.load : { status: 'loading' };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const regionsAndDefault = Promise.all([
      listAllRegions(api, signal),
      getBrandDefault(api, signal),
    ]);
    Promise.all([regionsAndDefault, getDistributionReport(api, signal)]).then(
      ([[regions, brandDefault], report]) =>
        setPage({
          status: 'loaded',
          value: { regions, brandDefault, cards: coverageCards(report) },
        }),
      (error: unknown) => {
        if (!signal.aborted) setPage({ status: 'failed', error });
      },
    );
    if (batchId !== null) {
      Promise.all([
        getBatch(api, batchId, signal).then((batch) =>
          getProductSetting(api, batch.productId, signal).then((product) => ({ batch, product })),
        ),
        regionsAndDefault,
      ]).then(
        ([{ batch, product }, [, brandDefault]]) =>
          setPicker({
            batchId,
            load: {
              status: 'loaded',
              value: {
                batch,
                productName: product.name,
                inherits: inherited(product.distribution, brandDefault),
              },
            },
          }),
        (error: unknown) => {
          if (!signal.aborted) setPicker({ batchId, load: { status: 'failed', error } });
        },
      );
    }
    return () => controller.abort();
  }, [api, batchId]);

  return (
    <>
      {page.status === 'loading' ? <Waiting>Loading regions</Waiting> : null}
      <PageHeader
        title="Distribution regions"
        subtitle="Where your goods are meant to be sold, so a unit surfacing elsewhere is noticed"
        action={
          <button className="vp-fill" type="button" style={newRegionButton}>
            New region
          </button>
        }
      />
      {page.status === 'failed' ? (
        <Card title="Regions could not be loaded" body={failureText(page.error)} />
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.25fr 1fr',
              gap: 16,
              alignItems: 'start',
            }}
          >
            <RegionTable
              regions={page.status === 'loaded' ? page.value.regions : []}
              busy={page.status === 'loading'}
            />
            {picker === null ? null : picker.status === 'failed' ? (
              <Card title="This batch could not be loaded" body={failureText(picker.error)} />
            ) : picker.status === 'loaded' && page.status === 'loaded' ? (
              <BatchPicker
                picker={picker.value}
                regions={page.value.regions}
                onSaved={(batch) =>
                  setPicker({
                    batchId: batch.id,
                    load: { status: 'loaded', value: { ...picker.value, batch } },
                  })
                }
              />
            ) : null}
          </div>
          {page.status === 'loaded' ? <Coverage cards={page.value.cards} /> : null}
        </>
      )}
    </>
  );
}

function RegionTable({ regions, busy }: { regions: Region[]; busy: boolean }) {
  return (
    <div role="table" aria-label="Regions" aria-busy={busy} style={panel}>
      <div
        role="row"
        style={{ ...grid, padding: '14px 22px', borderBottom: `1px solid ${colors.line}` }}
      >
        {['Region', 'Countries', 'Used by'].map((label) => (
          <div
            key={label}
            role="columnheader"
            style={{ ...type.tableHeader, color: colors.mutedInk }}
          >
            {label}
          </div>
        ))}
        <div role="columnheader" aria-label="Edit" />
      </div>
      {regions.map((region, i) => (
        <div key={region.id} role="rowgroup">
          {i > 0 ? <div style={{ height: 1, background: colors.line }} /> : null}
          <div role="row" style={{ ...grid, alignItems: 'center', padding: '15px 22px' }}>
            <div role="cell" style={{ ...type.bodyStrong, color: colors.navy }}>
              {region.name}
            </div>
            <div role="cell" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {region.countries.map((code) => (
                <span key={code} style={countryChip}>
                  {code}
                </span>
              ))}
            </div>
            {/* No route counts what uses a region; only a refused delete says, DEFERRED 4.2. */}
            <div role="cell" style={{ ...type.body, color: colors.mutedInk }} />
            <div role="cell" style={{ ...type.metaBold, color: colors.mutedInk }}>
              Edit
            </div>
          </div>
        </div>
      ))}
      <p
        style={{
          ...type.meta,
          color: colors.mutedInk,
          lineHeight: 1.55,
          margin: 0,
          padding: '16px 22px',
          borderTop: `1px solid ${colors.line}`,
        }}
      >
        A region still assigned to something cannot be deleted. Removing it silently would switch
        those batches to “nobody has said”, turning their checks off with nothing to show for it.
      </p>
    </div>
  );
}

type Choice = 'inherit' | 'region' | 'unrestricted';

const choiceOf = (setting: OwnSetting): Choice =>
  setting === undefined ? 'inherit' : setting.mode === 'region' ? 'region' : 'unrestricted';

function BatchPicker({
  picker,
  regions,
  onSaved,
}: {
  picker: Picker;
  regions: Region[];
  onSaved: (batch: Batch) => void;
}) {
  const { api } = useSession();
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const { batch, inherits } = picker;
  const choice = choiceOf(batch.distribution);
  const nameOf = (id: string) => regions.find((r) => r.id === id)?.name ?? '';
  const subtitle = [batchLabel(batch), picker.productName].filter(Boolean).join(' · ');

  const save = (distribution: DistributionSetting | null) => {
    setSaving(true);
    setRefusal(null);
    setBatchDistribution(api, batch.id, distribution).then(
      (saved) => {
        setSaving(false);
        onSaved(saved);
      },
      (error: unknown) => {
        setSaving(false);
        setRefusal(failureText(error));
      },
    );
  };

  const inheritedPill =
    inherits.status === 'region'
      ? nameOf(inherits.regionId)
      : inherits.status === 'unrestricted'
        ? 'Ships anywhere'
        : '';
  const ownRegion = batch.distribution?.mode === 'region' ? batch.distribution.regionId : '';
  const firstRegion = regions[0];

  return (
    <section aria-label="Where this batch ships" style={panel}>
      <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div style={{ ...type.panelTitle, color: colors.navy }}>Where this batch ships</div>
          {subtitle !== '' ? (
            <div style={{ ...type.meta, color: colors.mutedInk, paddingTop: 3 }}>{subtitle}</div>
          ) : null}
        </div>
        <div
          role="radiogroup"
          aria-label="Where this batch ships"
          aria-busy={saving}
          style={{ display: 'contents' }}
        >
          <Option
            label="Use my brand default"
            selected={choice === 'inherit'}
            disabled={saving}
            onSelect={() => save(null)}
            pill={
              inheritedPill === '' ? null : (
                <Pill selected={choice === 'inherit'}>{inheritedPill}</Pill>
              )
            }
          >
            Inherited: set once on the brand profile and it applies everywhere. Change it there and
            this follows.
          </Option>
          <Option
            label="A specific region"
            selected={choice === 'region'}
            disabled={saving || firstRegion === undefined}
            onSelect={() => {
              if (firstRegion !== undefined) save({ mode: 'region', regionId: firstRegion.id });
            }}
            pill={
              choice === 'region' ? (
                <select
                  aria-label="Region"
                  value={ownRegion}
                  disabled={saving}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => save({ mode: 'region', regionId: e.target.value })}
                  style={{
                    ...pillStyle(true),
                    border: 'none',
                    appearance: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {regions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.name}
                    </option>
                  ))}
                </select>
              ) : null
            }
          >
            Overrides the brand default for this batch only.
          </Option>
          <Option
            label="Ships anywhere"
            selected={choice === 'unrestricted'}
            disabled={saving}
            onSelect={() => save({ mode: 'unrestricted' })}
            pill={null}
          >
            A deliberate decision, not a blank. This batch will never be flagged as out of market,
            and it counts as configured in your coverage.
          </Option>
        </div>
        {refusal !== null ? <Rejection>{refusal}</Rejection> : null}
        <div
          style={{
            borderTop: `1px solid ${colors.line}`,
            paddingTop: 12,
            ...type.caption,
            color: colors.mutedInk,
            lineHeight: 1.55,
          }}
        >
          Editable after codes are printed, unlike the manufacturing details beside it: goods get
          re-routed. Past scans keep the answer they were given at the time.
        </div>
      </div>
    </section>
  );
}

function Option({
  label,
  selected,
  disabled,
  onSelect,
  pill,
  children,
}: {
  label: string;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  pill: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      aria-label={label}
      tabIndex={0}
      onClick={() => {
        if (!disabled && !selected) onSelect();
      }}
      onKeyDown={(e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !disabled && !selected) {
          e.preventDefault();
          onSelect();
        }
      }}
      style={{
        borderRadius: 12,
        border: `1px solid ${selected ? colors.brand : colors.line}`,
        background: selected ? selectedGround : colors.card,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
        cursor: disabled || selected ? 'default' : 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span
          aria-hidden="true"
          style={{
            width: 16,
            height: 16,
            boxSizing: 'border-box',
            borderRadius: 8,
            border: `2px solid ${selected ? colors.brand : colors.mutedInk}`,
            ...(selected
              ? { background: colors.brand, boxShadow: `inset 0 0 0 3px ${colors.white}` }
              : {}),
          }}
        />
        <span style={{ ...type.bodyBold, color: colors.navy }}>{label}</span>
        {pill}
      </div>
      <div style={{ ...type.caption, color: colors.mutedInk, lineHeight: 1.5, paddingLeft: 25 }}>
        {children}
      </div>
    </div>
  );
}

/** The board draws the unselected option's pill neutral and the selected one's brand, D11. */
const pillStyle = (selected: boolean): CSSProperties => ({
  display: 'inline-flex',
  padding: '4px 12px',
  borderRadius: radius.chip,
  background: selected ? chip.brand.bg : chip.neutral.bg,
  color: selected ? chip.brand.fg : chip.neutral.fg,
  ...type.captionBold,
});

function Pill({ selected, children }: { selected: boolean; children: ReactNode }) {
  return <span style={pillStyle(selected)}>{children}</span>;
}

function Coverage({ cards }: { cards: CoverageCard[] }) {
  return (
    <section aria-label="Coverage" style={panel}>
      <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <div style={{ ...type.panelTitle, color: colors.navy }}>Coverage</div>
          <div style={{ ...type.caption, color: colors.mutedInk }}>
            “Ships anywhere” counts as covered · only silence does not
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
          {cards.map((card) => (
            <div key={card.label} style={panel}>
              <div
                style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 6 }}
              >
                <div style={{ ...type.statLabel, color: colors.mutedInk }}>{card.label}</div>
                <div style={{ ...type.statValue, color: colors.navy }}>{card.value}</div>
                <div
                  style={{
                    ...type.captionStrong,
                    color: card.tone === 'suspect' ? verdict.suspect.fg : colors.mutedInk,
                  }}
                >
                  {card.note}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function failureText(error: unknown): string {
  if (error instanceof ApiError && error.kind === 'network') {
    return 'The API could not be reached. Reload the page to try again.';
  }
  if (error instanceof Error) return error.message;
  return 'Reload the page to try again.';
}

const panel: CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: radius.card,
};

const grid: CSSProperties = { display: 'grid', gridTemplateColumns: columns, gap: 14 };

const countryChip: CSSProperties = {
  display: 'inline-flex',
  padding: '4px 10px',
  borderRadius: 8,
  background: chip.neutral.bg,
  color: chip.neutral.fg,
  ...type.captionBold,
  fontFamily: fonts.mono,
};

const newRegionButton: CSSProperties = {
  height: shell.actionButton.height,
  padding: `0 ${shell.actionButton.paddingX}px`,
  borderRadius: shell.actionButton.radius,
  background: colors.brand,
  color: colors.white,
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  ...type.button,
};
